import { useEffect, useRef, useState } from "react";
import { ChevronDown, ShieldAlert, Sparkles, Trash2, Info, Lightbulb, Gauge } from "lucide-react";
import { api } from "@/lib/api";
import { useSlip } from "@/context/SlipContext";
import { useAuth } from "@/context/AuthContext";

const RISK = { conservative: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", balanced: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10", aggressive: "text-amber-300 border-amber-500/40 bg-amber-500/10", lottery: "text-red-300 border-red-500/40 bg-red-500/10" };

export function SlipInsights() {
  const { legs, wager, remove } = useSlip();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [aiRead, setAiRead] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const key = legs.map((l) => l.pick_id).join(",");
  const lastKey = useRef("");
  useEffect(() => {
    if (legs.length < 2) {
      setD(null);
      setAiRead(null);
      return;
    }
    if (lastKey.current !== key) setAiRead(null);
    lastKey.current = key;
    const t = setTimeout(() => api.post("/slip/insights", { pick_ids: legs.map((l) => l.pick_id), wager: Number(wager) || 25 }).then((r) => setD(r.data)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [key, wager, legs]);
  if (!d || legs.length < 2) return null;
  const issues = d.warnings.length;
  const loadAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/slip/insights", { pick_ids: legs.map((l) => l.pick_id), wager: Number(wager) || 25, ai: true });
      setAiRead(data.ai_read);
    } finally {
      setAiBusy(false);
    }
  };
  const scoreColor = d.score >= 65 ? "#10B981" : d.score >= 45 ? "#F59E0B" : "#EF4444";

  return (
    <div data-testid="slip-insights" className="border-b border-[#26334D] bg-[#0B0E14]/40">
      <button data-testid="slip-insights-toggle" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-5 py-2.5 text-left">
        <span className="relative flex h-8 w-8 shrink-0 items-center justify-center" data-testid="slip-score">
          <svg width="32" height="32" className="-rotate-90"><circle cx="16" cy="16" r="13" stroke="#26334D" strokeWidth="3" fill="none" /><circle cx="16" cy="16" r="13" stroke={scoreColor} strokeWidth="3" fill="none" strokeLinecap="round" strokeDasharray={2 * Math.PI * 13} strokeDashoffset={2 * Math.PI * 13 * (1 - d.score / 100)} className="transition-[stroke-dashoffset] duration-500" /></svg>
          <span className="absolute font-mono-num text-[10px] font-bold" style={{ color: scoreColor }}>{d.score}</span>
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-300"><Gauge size={12} className="text-cyan-400" /> Slip Insights
            <span data-testid="slip-risk" className={`rounded-full border px-2 py-0.5 text-[10px] ${RISK[d.risk]}`}>{d.risk}</span>
            {issues > 0 && <span data-testid="slip-warning-count" className="flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[10px] text-red-300"><ShieldAlert size={10} /> {issues}</span>}
          </span>
          <span className="block truncate text-[11px] text-slate-500" data-testid="slip-ev">EV <span className={`font-mono-num font-bold ${d.ev >= 0 ? "text-emerald-400" : "text-red-400"}`}>{d.ev >= 0 ? "+" : ""}{(d.ev * 100).toFixed(1)}%</span> · edge {(d.edge * 100).toFixed(1)} pts · {d.events} game{d.events !== 1 ? "s" : ""}</span>
        </span>
        <ChevronDown size={14} className={`text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="fade-up space-y-2 px-5 pb-3" data-testid="slip-insights-body">
          {d.warnings.map((w, i) => (
            <div key={i} data-testid="slip-warning" className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs leading-relaxed text-red-200"><ShieldAlert size={13} className="mt-0.5 shrink-0 text-red-400" /> {w.text}</div>
          ))}
          {d.notes.map((w, i) => (
            <div key={i} data-testid="slip-note" className="flex items-start gap-2 rounded-lg border border-cyan-500/25 bg-cyan-500/5 px-3 py-2 text-xs leading-relaxed text-cyan-100"><Info size={13} className="mt-0.5 shrink-0 text-cyan-400" /> {w.text}</div>
          ))}
          {d.suggestions.map((s, i) => (
            <div key={i} data-testid="slip-suggestion" className="flex items-start gap-2 rounded-lg border border-[#26334D] bg-[#121824] px-3 py-2 text-xs leading-relaxed text-slate-300">
              <Lightbulb size={13} className="mt-0.5 shrink-0 text-amber-400" /> <span className="flex-1">{s.text}</span>
              {s.action === "remove" && s.pick_ids[0] && <button data-testid="slip-suggestion-remove" onClick={() => remove(s.pick_ids[0])} className="shrink-0 rounded p-1 text-slate-400 hover:bg-red-500/15 hover:text-red-300" title="Remove this leg"><Trash2 size={13} /></button>}
            </div>
          ))}
          <div className="rounded-lg border border-[#26334D] bg-[#121824] px-3 py-2">
            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-cyan-400"><Sparkles size={11} /> Claude's read</p>
            {aiRead ? <p className="mt-1 text-xs leading-relaxed text-slate-200" data-testid="slip-ai-read">{aiRead}</p>
              : d.ai_available ? <button data-testid="slip-ai-btn" onClick={loadAi} disabled={aiBusy} className="mt-1 text-xs text-cyan-300 underline disabled:opacity-60">{aiBusy ? "Reading the ticket…" : "Get a two-sentence read before you save"}</button>
              : <p className="mt-1 text-xs text-slate-500" data-testid="slip-ai-locked">Claude's read on your slip is available on Basic & Premium.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
